import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { statusLabel, statusTone } from "../lib/format";
import { Plus, Search, Pencil, CornerDownRight, Kanban, Upload, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import EditProjectModal from "../components/EditProjectModal";
import { designImageSrc } from "../components/DesignImage";
import ImportSitesDialog from "./projects/ImportSitesDialog";
import FilteredExportButton from "../components/FilteredExportButton";
import { Pager } from "../components/Pager";
import { CLASS_BY_KEY, resolveClass } from "../lib/classification";

function ClassCell({ value }) {
  const c = CLASS_BY_KEY[resolveClass(value)];
  const Icon = c.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: c.tone }}>
      <Icon size={13} /> {c.label}
    </span>
  );
}

/** Resolve absolute CAPEX £ mirroring the backend priority chain. */
function resolveCapex(p) {
  const direct = p?.capex_gbp;
  if (direct != null && Number(direct) > 0) return Number(direct);
  const kwp = Number(p?.system_size_kwp || 0);
  if (!kwp) return 0;
  const perKwp = Number(p?.capex_per_kwp_gbp ?? 850);
  return kwp * perKwp;
}
const GBP0 = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });

/** Human-friendly label for the `data_source` field on a project. */
const SYSTEM_TYPE_LABELS = {
  solaredge: "SolarEdge",
  solis: "Solis",
  meteronline: "MeterOnline",
  other: "Other",
};
export const systemTypeLabel = (v) => SYSTEM_TYPE_LABELS[v] || v || "—";

const STATUS_CHIPS = [["all", "All"], ["design", "Design"], ["installing", "Installing"], ["operational", "Operational"]];
const statusGroup = (st) => (["monitoring", "completed"].includes(st) ? "operational"
  : ["approved", "installing"].includes(st) ? "installing" : st === "cancelled" ? "cancelled" : "design");

const SORT_STATUS_ORDER = ["design", "quote_draft", "quote_sent", "approved", "installing", "monitoring", "completed", "cancelled"];
const SORT_GETTERS = {
  classification: (p) => CLASS_BY_KEY[resolveClass(p.business_classification)]?.label || "",
  status: (p) => SORT_STATUS_ORDER.indexOf(p.status || "design"),
  capex: (p) => resolveCapex(p),
  size: (p) => Number(p.system_size_kwp || 0),
};

function SortTh({ col, label, sort, onSort, className = "" }) {
  const active = sort.key === col;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(col)} data-testid={`projects-sort-${col}`}
        className="inline-flex items-center gap-1 hover:opacity-80"
        style={{ font: "inherit", letterSpacing: "inherit", color: "inherit", textTransform: "inherit" }}
        title={`Sort by ${label}`}>
        {label} <Icon size={12} className={active ? "" : "opacity-40"} />
      </button>
    </th>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [chip, setChip] = useState("all");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const onSort = (col) => setSort(s => s.key !== col ? { key: col, dir: "asc" } : s.dir === "asc" ? { key: col, dir: "desc" } : { key: null, dir: "asc" });
  const [perPage, setPerPage] = useState(() => { const v = +localStorage.getItem("eneprox.projects.perPage"); return [25, 50, 100].includes(v) ? v : 25; });
  const [editing, setEditing] = useState(null);
  const [stages, setStages] = useState([]);          // active PM stages [{key,label,...}]
  const [savingStage, setSavingStage] = useState({});// pid -> bool
  const [bulkOpen, setBulkOpen] = useState(false);
  const canBulkImport = user && (user.role === "admin" || user.role === "manager");
  const canDelete = ["admin", "manager"].includes(user?.role);
  const canSetStage = canDelete;                     // same auth gate

  const load = () => api.get("/projects").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/pm/roles").then(r => setStages(r.data.statuses || [])).catch(() => {});
  }, []);

  const stageLabelFor = (key) =>
    stages.find(s => s.key === key)?.label || key || "—";

  // Editable status list — kept in sync with backend Project.status
  // enum. Ordered so the "happy path" reads top→bottom.
  const STATUS_OPTIONS = [
    "design", "quote_draft", "quote_sent", "approved",
    "installing", "monitoring", "completed", "cancelled",
  ];

  const [savingStatus, setSavingStatus] = useState({});
  const changeStatus = async (project, newStatus) => {
    if (!newStatus || newStatus === project.status) return;
    // Optimistic UI — flip the row locally, roll back on error.
    const prevStatus = project.status;
    setRows(prev => prev.map(r => r.id === project.id ? { ...r, status: newStatus } : r));
    setSavingStatus(m => ({ ...m, [project.id]: true }));
    try {
      await api.patch(`/projects/${project.id}`, { status: newStatus });
      toast.success(`${project.name} → ${statusLabel(newStatus)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not change status");
      setRows(prev => prev.map(r => r.id === project.id ? { ...r, status: prevStatus } : r));
    } finally {
      setSavingStatus(m => ({ ...m, [project.id]: false }));
    }
  };

  const moveToStage = async (project, newKey) => {
    const cur = project.lifecycle_status || stages[0]?.key || "solar_request";
    if (!newKey || newKey === cur) return;
    // Final Handover needs a date — bail out with a friendly toast so the
    // operator opens the project detail (or PM board) to complete it there.
    if (newKey === "final_handover" && !project.handover_date) {
      toast.error("Set a handover date on the project before completing it — open Edit or use the PM board move dialog.");
      return;
    }
    setSavingStage(m => ({ ...m, [project.id]: true }));
    try {
      await api.patch(`/projects/${project.id}/lifecycle`, { to_stage: newKey });
      toast.success(`${project.name} → ${stageLabelFor(newKey)}. Notifications sent.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not move project");
    } finally {
      setSavingStage(m => ({ ...m, [project.id]: false }));
    }
  };

  const searched = rows.filter(r =>
    !q || r.name.toLowerCase().includes(q.toLowerCase()) ||
    r.customer_name.toLowerCase().includes(q.toLowerCase()) ||
    (r.postcode || "").toLowerCase().includes(q.toLowerCase())
  );
  const chipCounts = searched.reduce((acc, r) => {
    const g = statusGroup(r.status);
    acc[g] = (acc[g] || 0) + 1;
    return acc;
  }, { all: searched.length });
  const filtered = chip === "all" ? searched : searched.filter((r) => statusGroup(r.status) === chip);

  // Order rows as a shallow tree: every top-level (or orphan-parent)
  // project is listed, then its direct children indented underneath.
  // Depth is capped by the parent-cycle guard on the backend, so a
  // one-pass build is safe.
  const ordered = useMemo(() => {
    const byId = new Map(filtered.map(r => [r.id, r]));
    const childrenOf = new Map();
    for (const r of filtered) {
      if (r.parent_project_id && byId.has(r.parent_project_id)) {
        const arr = childrenOf.get(r.parent_project_id) || [];
        arr.push(r);
        childrenOf.set(r.parent_project_id, arr);
      }
    }
    const roots = filtered.filter(
      r => !r.parent_project_id || !byId.has(r.parent_project_id));
    const get = SORT_GETTERS[sort.key];
    const cmp = (a, b) => {
      if (get) {
        const va = get(a), vb = get(b);
        const d = typeof va === "string" ? va.localeCompare(vb) : va - vb;
        if (d) return sort.dir === "asc" ? d : -d;
      }
      return a.name.localeCompare(b.name);
    };
    if (get) roots.sort(cmp);
    const out = [];
    const walk = (r, depth) => {
      out.push({ ...r, _depth: depth });
      const kids = (childrenOf.get(r.id) || []).sort(cmp);
      for (const k of kids) walk(k, depth + 1);
    };
    for (const root of roots) walk(root, 0);
    return out;
  }, [filtered, sort]);

  useEffect(() => { setPage(1); }, [q, chip, sort]);
  const pages = Math.max(1, Math.ceil(ordered.length / perPage));
  const curPage = Math.min(page, pages);
  const pageRows = ordered.slice((curPage - 1) * perPage, curPage * perPage);
  const goPage = (n) => {
    setPage(Math.min(Math.max(1, n), pages));
    document.querySelector('[data-testid="projects-table"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePerPage = (n) => { setPerPage(n); setPage(1); localStorage.setItem("eneprox.projects.perPage", String(n)); };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="label mb-2">Portfolio</div>
          <h1 className="font-display text-4xl font-bold">Projects</h1>
          <p className="muted text-sm mt-2">All sites across design, quote, install and 20-year monitoring.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/pm"
            className="btn-ghost text-xs"
            data-testid="projects-open-kanban-btn"
            title="Open the Lifecycle Board (Kanban) for these projects"
          >
            <Kanban size={13} /> Kanban
          </Link>
          {canBulkImport && (
            <button
              onClick={() => setBulkOpen(true)}
              className="btn-ghost inline-flex items-center gap-1.5"
              data-testid="projects-bulk-import-btn"
              title="Import sites from Excel — historical/operational sites or new projects"
            >
              <Upload size={14} /> Import sites
            </button>
          )}
          <Link to="/projects/new" className="btn-accent" data-testid="projects-new-btn">
            <Plus size={14} /> New Project
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 input-box max-w-sm">
          <Search size={14} className="muted" />
          <input
            data-testid="projects-search-input"
            className="flex-1 outline-none bg-transparent text-sm"
            placeholder="Search by name, customer, postcode…"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist" data-testid="projects-status-chips">
          {STATUS_CHIPS.map(([k, label]) => (
            <button key={k} onClick={() => setChip(k)} data-testid={`projects-chip-${k}`} aria-pressed={chip === k}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${chip === k
                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                : "bg-[var(--panel)] border-[var(--line)] hover:border-[var(--accent)]"}`}>
              {label}<span className="ml-1.5 font-mono opacity-70" data-testid={`projects-chip-count-${k}`}>{chipCounts[k] || 0}</span>
            </button>
          ))}
        </div>
        <div className="label">{filtered.length} site{filtered.length === 1 ? "" : "s"}</div>
        <div className="flex-1" />
        <FilteredExportButton
          ids={ordered.map((p) => p.id)}
          title="Projects list"
          testIdPrefix="projects-export"
          layoutScope="projects-list"
        />
      </div>

      <div className="swiss-card-flat overflow-hidden">
        <table className="data-table" data-testid="projects-table">
          <thead>
            <tr>
              <th>Project</th>
              <SortTh col="classification" label="Business classification" sort={sort} onSort={onSort} />
              <th>Postcode</th>
              <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
              <th>Lifecycle</th>
              <SortTh col="capex" label="CAPEX" sort={sort} onSort={onSort} className="num" />
              <th>System type</th>
              <SortTh col="size" label="System size (kWp DC)" sort={sort} onSort={onSort} className="num" />

              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(p => (
              <tr key={p.id} data-testid={`project-row-${p.id}`}>
                <td className="font-medium">
                  <div className="flex items-center gap-2" style={{ paddingLeft: p._depth * 20 }}>
                    {p._depth > 0 && (
                      <CornerDownRight size={12} className="text-[var(--accent)]" aria-hidden />
                    )}
                    {p.design_image_url && (
                      <img src={designImageSrc(p.design_image_url)} alt=""
                           className="w-10 h-7 object-cover rounded border shrink-0"
                           style={{ borderColor: "var(--line)" }}
                           data-testid={`project-thumb-${p.id}`} />
                    )}
                    <span>{p.name}</span>
                    {p.children_count > 0 && (
                      <span className="tag" title={`${p.children_count} sub-site${p.children_count === 1 ? "" : "s"}`}
                            data-testid={`project-children-badge-${p.id}`}>
                        {p.children_count} sub-site{p.children_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </td>
                <td data-testid={`project-class-${p.id}`}><ClassCell value={p.business_classification} /></td>
                <td className="font-mono text-xs" data-testid={`project-postcode-${p.id}`}>{p.postcode || "—"}</td>
                <td data-testid={`project-status-cell-${p.id}`}>
                  {canSetStage ? (
                    <select
                      className={`input-swiss text-xs !py-1 tag ${statusTone(p.status)}`}
                      style={{ minWidth: 110 }}
                      value={p.status || "design"}
                      onChange={(e) => changeStatus(p, e.target.value)}
                      disabled={!!savingStatus[p.id]}
                      title="Change status"
                      data-testid={`project-status-select-${p.id}`}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{statusLabel(s)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`tag ${statusTone(p.status)}`}>{statusLabel(p.status)}</span>
                  )}
                </td>
                <td data-testid={`project-lifecycle-cell-${p.id}`}>
                  {stages.length === 0 ? (
                    <span className="muted text-xs">—</span>
                  ) : canSetStage ? (
                    <select
                      className="input-swiss text-xs !py-1"
                      value={p.lifecycle_status || stages[0]?.key || ""}
                      disabled={!!savingStage[p.id]}
                      onChange={(e) => moveToStage(p, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`project-lifecycle-select-${p.id}`}
                      title="Move this project to a different lifecycle stage — the PM Kanban will update immediately"
                    >
                      {stages.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="tag"
                      style={{ background: "#DDF3F1", color: "#0F7A73" }}
                      data-testid={`project-lifecycle-badge-${p.id}`}
                    >
                      {stageLabelFor(p.lifecycle_status)}
                    </span>
                  )}
                </td>
                <td className="num font-mono text-xs" data-testid={`project-capex-${p.id}`}
                    title={
                      (p.capex_gbp != null && Number(p.capex_gbp) > 0)
                        ? "Direct £ override"
                        : `£${Number(p.capex_per_kwp_gbp ?? 850)}/kWp × ${Number(p.system_size_kwp || 0).toFixed(2)} kWp`
                    }>
                  {resolveCapex(p) > 0 ? GBP0.format(resolveCapex(p)) : <span className="muted">—</span>}
                </td>
                <td className="text-xs">
                  {p.data_source ? (
                    <span className="tag signal">{systemTypeLabel(p.data_source)}</span>
                  ) : p.solaredge_site_id ? (
                    <span className="tag signal">SolarEdge</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                  {p.data_source === "solaredge" && p.solaredge_site_id && (
                    <div className="font-mono text-[10px] muted mt-0.5">{p.solaredge_site_id}</div>
                  )}
                </td>
                <td className="num font-mono text-xs" data-testid={`project-kwp-${p.id}`}>
                  {p.system_size_kwp ? Number(p.system_size_kwp).toLocaleString("en-GB", { maximumFractionDigits: 2 }) : "—"}
                </td>
                <td className="num">
                  <div className="flex justify-end items-center gap-2">
                    <button
                      onClick={() => setEditing(p)}
                      className="btn-ghost !px-2.5"
                      data-testid={`edit-project-${p.id}`}
                      title="Edit / delete"
                      aria-label={`Edit ${p.name}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <Link to={`/projects/${p.id}`} className="btn-ghost" data-testid={`open-project-${p.id}`}>
                      Open <ChevronRight size={13} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {!ordered.length && (
              <tr><td colSpan={9} className="muted text-center py-12">No projects match.</td></tr>
            )}
          </tbody>
        </table>
        {ordered.length > 0 && (
          <Pager total={ordered.length} page={curPage} perPage={perPage} onPage={goPage} onPerPage={changePerPage} testid="projects-pager" />
        )}
      </div>

      {editing && (
        <EditProjectModal
          project={editing}
          canDelete={canDelete}
          onClose={() => setEditing(null)}
          onSaved={load}
          onDeleted={load}
        />
      )}
      {bulkOpen && (
        <ImportSitesDialog
          onClose={() => setBulkOpen(false)}
          onImported={() => load()}
        />
      )}
    </div>
  );
}
